import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient'; // Import supabase client

const ClientForm = ({ client, onSave, onCancel, isSaving }) => {
  // Form state uses v_client_summary / customers column names directly
  const [formData, setFormData] = useState({ full_name: '', phone_number: '', id: '' });
  const [phoneAvailability, setPhoneAvailability] = useState('');
  const [idAvailability, setIdAvailability] = useState('');
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [checkingId, setCheckingId] = useState(false);
  // true when editing an existing client (lock auto-generation), false for new clients
  const [isIdLocked, setIsIdLocked] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        full_name: (client.full_name || ''),
        phone_number: (client.phone_number || ''),
        id: (client.id || '')
      });
      setIsIdLocked(true); // Lock ID auto-generation when editing an existing client
    } else {
      setFormData({ full_name: '', phone_number: '', id: '' });
      setIsIdLocked(false); // Allow ID auto-generation for new clients
    }
  }, [client]);

  const generateClientId = useCallback((name) => {
    const parts = name.split(' ').filter(Boolean);
    let id = '';

    // Take initials first
    for (let i = 0; i < parts.length && id.length < 4; i++) {
      id += parts[i][0];
    }

    // If still too short, add more characters from the name
    let charIndex = 1;
    while (id.length < 4) {
      let addedCharInIteration = false;
      for (let i = 0; i < parts.length && id.length < 4; i++) {
        if (parts[i].length > charIndex) {
          id += parts[i][charIndex];
          addedCharInIteration = true;
        }
      }
      if (!addedCharInIteration) { // If no characters were added in this iteration, break to avoid infinite loop
        break;
      }
      charIndex++;
    }

    // If still too short, append random digits
    while (id.length < 4) {
      id += Math.floor(Math.random() * 10).toString();
    }

    // Ensure it's between 1 and 4 characters
    return id.substring(0, Math.min(id.length, 4)).toUpperCase();
  }, []);

  // field is the actual customers column name: 'phone_number' or 'id'
  const checkAvailability = useCallback(async (field, value) => {
    if (!value) {
      if (field === 'phone_number') setPhoneAvailability('');
      if (field === 'id') setIdAvailability('');
      return;
    }

    if (field === 'phone_number') setCheckingPhone(true);
    if (field === 'id') setCheckingId(true);

    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, phone_number')
        .eq(field, value)
        .limit(1);

      if (error) throw error;

      // When editing, ignore the match if it's the current client's own record
      const isDuplicate = data.length > 0 && (client ? data[0].id !== client.id : true);

      if (field === 'phone_number') {
        setPhoneAvailability(isDuplicate ? 'Duplicate' : 'Available');
      }
      if (field === 'id') {
        setIdAvailability(isDuplicate ? 'Duplicate' : 'Available');
      }
    } catch (err) {
      console.error(`Error checking ${field} availability:`, err);
      if (field === 'phone_number') setPhoneAvailability('Error');
      if (field === 'id') setIdAvailability('Error');
    } finally {
      if (field === 'phone_number') setCheckingPhone(false);
      if (field === 'id') setCheckingId(false);
    }
  }, [client]);

  // Debounced phone availability check — only fires when value differs from the client's existing phone
  useEffect(() => {
    const handler = setTimeout(() => {
      if (formData.phone_number !== client?.phone_number) {
        checkAvailability('phone_number', formData.phone_number);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [formData.phone_number, checkAvailability, client?.phone_number]);

  // Debounced ID availability check — only fires when value differs from the client's existing id
  useEffect(() => {
    const handler = setTimeout(() => {
      if (formData.id !== client?.id) {
        checkAvailability('id', formData.id);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [formData.id, checkAvailability, client?.id]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'id') {
      setIsIdLocked(true); // User is manually editing id, stop auto-generation
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-generate ID from name only for new clients and only if user hasn't manually set it
    if (name === 'full_name' && !client && isIdLocked === false) {
      const newId = generateClientId(value);
      setFormData(prev => ({ ...prev, id: newId }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // When editing: isIdLocked is true (started with original ID), skip ID duplicate block
    // When creating: both phone and ID duplicate checks apply
    if ((client && (phoneAvailability === 'Duplicate' || (idAvailability === 'Duplicate' && !isIdLocked))) ||
        (!client && (phoneAvailability === 'Duplicate' || idAvailability === 'Duplicate'))) {
      alert('Resolvé el teléfono o ID duplicado antes de guardar.');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4">{client ? 'Editar Cliente' : 'Agregar Nuevo Cliente'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">Nombre Completo</label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name || ''}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
              Teléfono
              {checkingPhone && <span className="text-blue-500 ml-2">Verificando...</span>}
              {phoneAvailability === 'Available' && <span className="text-green-500 ml-2">Disponible</span>}
              {phoneAvailability === 'Duplicate' && <span className="text-red-500 ml-2">Duplicado</span>}
            </label>
            <input
              type="text"
              id="phone_number"
              name="phone_number"
              value={formData.phone_number || ''}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
          <div className="mb-6">
            <label htmlFor="id" className="block text-sm font-medium text-gray-700">
              ID de Cliente
              {checkingId && <span className="text-blue-500 ml-2">Verificando...</span>}
              {idAvailability === 'Available' && <span className="text-green-500 ml-2">Disponible</span>}
              {idAvailability === 'Duplicate' && <span className="text-red-500 ml-2">Duplicado</span>}
            </label>
            <input
              type="text"
              id="id"
              name="id"
              value={formData.id || ''}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              maxLength="4" // Enforce max length at UI level
              required
            />
          </div>
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={isSaving || checkingPhone || checkingId || phoneAvailability === 'Duplicate' || idAvailability === 'Duplicate' || (formData.id || '').length < 1 || (formData.id || '').length > 4}
            >
              {isSaving ? 'Guardando...' : 'Guardar Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientForm;
